import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import useSessionTrackingStore from '@/stores/sessionTrackingStore';
import useUIStore from '@/stores/uiStore';
import { useAuth } from '@/hooks';
import { useRoleSwitcher } from '@/features/auth/hooks/useRoleSwitcher';
import { useSchool } from '@/contexts/SchoolContext';
import { functions, httpsCallable } from '@/firebase';
import { useDurableActionSession } from '@/features/discipline/hooks/useDurableActionSession';

import ConcludeSessionBar from '@/features/discipline/components/queue/ConcludeSessionBar';
import ConcludeSessionModal from '@/features/discipline/components/modals/ConcludeSessionModal';
import SessionActiveIndicator from './SessionActiveIndicator';

/** Show a warning toast after 15 minutes of inactivity. */
export const INACTIVITY_MS = 15 * 60 * 1000;

/** Legacy browser-local safety window retained for rollout compatibility. */
export const AUTO_CONCLUDE_MS = 4 * 60 * 60 * 1000;

/** Polling interval for the inactivity check. */
const POLL_INTERVAL_MS = 60_000;

/**
 * App-shell component that renders the session footer on every protected
 * route when a discipline session is active.
 *
 * Mount inside AppLayout so it is never unmounted on navigation.
 */
export default function GlobalSessionFooter() {
  const { user } = useAuth();
  const { currentRole } = useRoleSwitcher();
  const { schoolId } = useSchool();
  const navigate = useNavigate();
  const {
    serverSessionId,
    serverSessionStatus,
    serverSessionRecovery,
    concludeServerSession,
  } = useDurableActionSession();

  const [isConcludeModalOpen, setIsConcludeModalOpen] = useState(false);
  const [concludeSaving, setConcludeSaving] = useState(false);

  // --- Store subscriptions ---
  const sessionActionsArr = useSessionTrackingStore((state) => state.sessionActions);
  const sessionActionedIds = useSessionTrackingStore((state) => state.sessionActionedIds);
  const sessionRosterRecordIds = useSessionTrackingStore((state) => state.sessionRosterRecordIds);
  const sessionStartTime = useSessionTrackingStore((state) => state.sessionStartTime);
  const sessionBarDismissed = useSessionTrackingStore((state) => state.sessionBarDismissed);

  const isActive = Boolean(serverSessionId) || sessionActionsArr.length > 0;
  useEffect(() => {
    setIsConcludeModalOpen(false);
  }, [serverSessionId, isActive]);
  const remainingCount = useMemo(() => {
    if (!serverSessionId) return 0;
    const actionedIds = new Set(sessionActionedIds);
    return sessionRosterRecordIds.filter(recordId =>
      !actionedIds.has(recordId)
    ).length;
  }, [serverSessionId, sessionActionedIds, sessionRosterRecordIds]);
  const isRosterComplete = Boolean(serverSessionId) &&
    sessionRosterRecordIds.length > 0 && remainingCount === 0;

  // Convert entries array → Map for ConcludeSessionModal (expects .forEach(action, eventId))
  const sessionActionsMap = useMemo(() => new Map(sessionActionsArr), [sessionActionsArr]);

  // Compute session summary
  const sessionSummary = useMemo(() => {
    const counts = { attended: 0, missed: 0, late: 0, absent: 0, other: 0, total: 0 };
    sessionActionsArr.forEach(([, action]) => {
      counts.total++;
      switch (action.status) {
        case 'served': counts.attended++; break;
        case 'missed': counts.missed++; break;
        case 'late':   counts.late++;    break;
        case 'absent': counts.absent++;  break;
        default:       counts.other++;
      }
    });
    return counts;
  }, [sessionActionsArr]);

  // --- Conclude handler ---
  // Reads user/schoolId from closure — kept up-to-date via ref so effects
  // always call the latest version without stale captures.
  const handleConclude = useCallback(
    async ({ autoConcluded = false, autoConcludedReason = null, notes = '' } = {}) => {
      const store = useSessionTrackingStore.getState();
      const isCurrentRequest = () => {
        const current = useSessionTrackingStore.getState();
        return store.serverSessionId
          ? current.serverSessionId === store.serverSessionId
          : !current.serverSessionId && current.sessionActions === store.sessionActions;
      };

      setConcludeSaving(true);
      try {
        if (store.serverSessionId) {
          await concludeServerSession(notes || '');
          if (isCurrentRequest()) setIsConcludeModalOpen(false);
          return;
        }

        // Build actions object (CF expects plain object keyed by eventId)
        const actionsObj = {};
        store.sessionActions.forEach(([eventId, action]) => {
          actionsObj[eventId] = action;
        });

        // Derive whether this is a single-type or multi-type session from the
        // per-action `type` field. The server has final say (looks up labels
        // and overrides conductedBy), so this is a best-effort hint.
        const uniqueTypes = new Set();
        store.sessionActions.forEach(([, action]) => {
          if (action?.type && action.type !== 'unknown') uniqueTypes.add(action.type);
        });
        const isMultiType = uniqueTypes.size > 1;

        const payload = {
          schoolId,
          consequenceType: isMultiType
            ? 'mixed'
            : store.primaryConsequenceType || [...uniqueTypes][0] || 'mixed',
          consequenceTypeLabel: isMultiType
            ? 'Multiple types'
            : store.primaryConsequenceTypeLabel || null,
          conductedBy: {
            uid: user?.uid || null,
            displayName: user?.displayName || user?.email || 'Unknown',
            name: user?.displayName || user?.email || 'Unknown',
          },
          actions: actionsObj,
          sessionStartedAt: store.sessionStartTime,
          ...(store.sessionRosterRecordIds.length ? {
            rosterRecordIds: store.sessionRosterRecordIds,
          } : {}),
          notes: notes || '',
        };

        if (autoConcluded) {
          payload.autoConcluded = true;
        }
        if (autoConcludedReason) {
          payload.autoConcludedReason = autoConcludedReason;
        }

        const saveReport = httpsCallable(functions, 'saveSessionReport');
        await saveReport(payload);
        if (!isCurrentRequest()) return;

        store.concludeSession({
          consequenceType: payload.consequenceType,
          consequenceTypeLabel: payload.consequenceTypeLabel,
        });
        setIsConcludeModalOpen(false);

        if (autoConcluded) {
          useUIStore.getState().showInfo?.('Session auto-concluded due to inactivity');
        }
      } catch (error) {
        if (!isCurrentRequest()) return;
        console.error('[GlobalSessionFooter] Error saving session report:', error);
        useUIStore.getState().showError?.('Failed to save session report');
      } finally {
        setConcludeSaving(false);
      }
    },
    [user, schoolId, concludeServerSession]
  );

  // --- Inactivity / auto-conclude polling ---
  useEffect(() => {
    if (!isActive) return;

    const intervalId = setInterval(() => {
      const store = useSessionTrackingStore.getState();
      if (!store.isSessionActive()) return;

      const now = Date.now();
      const inactiveMs = now - (store.lastActivityAt || now);

      // Inactivity reminder at 15 min (fire only once per session)
      if (inactiveMs >= INACTIVITY_MS && !store.inactivityReminderShown) {
        useUIStore.getState().showWarning(
          'Your session remains open. Resume its register or conclude when complete.'
        );
        store.markInactivityReminderShown();
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isActive]);

  // A role switch changes visibility, not the declared sitting. The same
  // server session remains available when staff return; only an explicit
  // conclusion or the server recovery lifecycle can close it.

  // --- Guard: do not render for unauthenticated or parent users,
  //            or when no session is running. ---
  if (!user || !isActive || currentRole === 'parent') return null;

  const store = useSessionTrackingStore.getState();

  return (
    <>
      {!sessionBarDismissed ? (
        <ConcludeSessionBar
          sessionSummary={sessionSummary}
          sessionStartTime={sessionStartTime}
          sessionStatus={serverSessionStatus}
          recovery={serverSessionRecovery}
          consequenceTypeLabel={store.primaryConsequenceTypeLabel}
          remainingCount={remainingCount}
          requireComplete={Boolean(serverSessionId)}
          onConclude={() => setIsConcludeModalOpen(true)}
          onDismiss={() => useSessionTrackingStore.getState().setSessionBarDismissed(true)}
          onGoToQueue={() => navigate('/discipline/queue')}
        />
      ) : (
        <SessionActiveIndicator />
      )}

      <ConcludeSessionModal
        isOpen={isConcludeModalOpen}
        onClose={() => setIsConcludeModalOpen(false)}
        sessionActions={sessionActionsMap}
        sessionSummary={sessionSummary}
        conductedBy={user?.displayName || user?.email}
        consequenceTypeLabel={store.primaryConsequenceTypeLabel}
        sessionComplete={isRosterComplete}
        remainingCount={remainingCount}
        requireComplete={Boolean(serverSessionId)}
        onConfirm={(notes) => handleConclude({ notes })}
        saving={concludeSaving}
      />
    </>
  );
}
